import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  loading$ = new Subject<boolean>();

  // Fires each time a request finishes (success or error). The click guard listens to it to
  // re-enable a button once the requests its click started have all finished.
  readonly requestSettled$ = new Subject<void>();

  private totalRequests = 0;
  private spinnerDelay = 500;

  // Every request gets the next id, so a caller can note the last id at some moment and later
  // ask whether anything started AFTER that moment is still running.
  private nextRequestId = 0;
  private readonly pendingRequestIds = new Set<number>();

  /** The id of the most recently started request (0 before the first). */
  get lastRequestId(): number {
    return this.nextRequestId;
  }

  startLoading(): number {
    this.totalRequests++;
    const requestId = ++this.nextRequestId;
    this.pendingRequestIds.add(requestId);

    setTimeout(() => {
      if (this.totalRequests > 0) {
        this.loading$.next(this.anyRequests);
      }
    }, this.spinnerDelay);

    return requestId;
  }

  stopLoading(requestId?: number) {
    this.totalRequests--;
    if (requestId !== undefined) {
      this.pendingRequestIds.delete(requestId);
    }

    if (this.totalRequests === 0) {
      this.loading$.next(this.anyRequests);
    }

    this.requestSettled$.next();
  }

  /** Whether any request started after the one with this id is still running. */
  hasPendingSince(requestId: number): boolean {
    for (const pendingId of this.pendingRequestIds) {
      if (pendingId > requestId) {
        return true;
      }
    }
    return false;
  }

  private get anyRequests(): boolean {
    return this.totalRequests > 0;
  }
}
